import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Clock, CheckCircle, XCircle, ArrowLeft, ArrowRight, Swords } from 'lucide-react';
import { quizService } from '../services/quizService';
import { bookService } from '../services/bookService';
import { useToast } from '../hooks/use-toast';

const Quiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const challengeId = searchParams.get("challenge");
  const { toast } = useToast();
  
  const [quiz, setQuiz] = useState(null);
  const [book, setBook] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [challengeUnavailable, setChallengeUnavailable] = useState(false);

  useEffect(() => {
    fetchQuizData();
  }, [quizId]);

  useEffect(() => {
    if (!challengeId) {
      setChallenge(null);
      setChallengeUnavailable(false);
      return;
    }
    let cancelled = false;
    quizService.getChallenge(challengeId)
      .then((data) => { if (!cancelled) setChallenge(data); })
      .catch(() => { if (!cancelled) setChallengeUnavailable(true); });
    return () => { cancelled = true; };
  }, [challengeId]);

  useEffect(() => {
    let timer;
    if (quizStarted && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            // Time's up - auto submit
            handleSubmitQuiz();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => clearInterval(timer);
  }, [quizStarted, timeLeft]);

  const fetchQuizData = async () => {
    try {
      setLoading(true);
      const [quizData, questionsData] = await Promise.all([
        quizService.getQuiz(quizId),
        quizService.getQuizQuestions(quizId)
      ]);
      
      setQuiz(quizData);
      setQuestions(questionsData);
      setTimeLeft(quizData.time_limit * 60); // Convert to seconds

      try {
        const bookData = await bookService.getBook(quizData.book_id);
        setBook(bookData);
      } catch (bookError) {
        setBook({ id: quizData.book_id, title: 'Unknown Book' });
      }

    } catch (error) {
      console.error('Error fetching quiz data:', error);
      toast({
        title: "Error",
        description: "Failed to load quiz. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (answer) => {
    setAnswers({
      ...answers,
      [questions[currentQuestion].id]: answer
    });
  };

  const handleNextQuestion = () => {
    /* istanbul ignore else -- the Next button is hidden on the last question */
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePreviousQuestion = () => {
    /* istanbul ignore else -- the Previous button is disabled on the first question */
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    try {
      const submissionData = {
        answers: answers,
        time_taken: (quiz.time_limit * 60) - timeLeft,
        user_email: null, // Anonymous for now
        challenge_id: challenge?.id || null
      };

      const result = await quizService.submitQuiz(quizId, submissionData);
      
      // Store results in localStorage for the result page
      localStorage.setItem('quizResult', JSON.stringify({
        quizId,
        bookTitle: /* istanbul ignore next -- book is always set alongside quiz */ book?.title || 'Unknown Book',
        quizTitle: quiz.title,
        score: result.score,
        correct: result.correct,
        total: result.total,
        answers: result.user_answers,
        questions: result.questions,
        challengeId: challenge?.id || null,
        challengeTargetScore: challenge?.target_score ?? null
      }));
      
      navigate(`/quiz/${quizId}/result`);
      
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast({
        title: "Error",
        description: "Failed to submit quiz. Please try again.",
        variant: "destructive",
      });
    }
  };

  const startQuiz = () => {
    setQuizStarted(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!quiz || questions.length === 0) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Quiz not available</h2>
            <p className="text-gray-600 mb-4">
              {!quiz ? 'Quiz not found' : 'This quiz has no questions yet'}
            </p>
            <Button onClick={() => navigate('/')} variant="outline" className="min-h-[44px] touch-manipulation">
              Back to Home
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const quizPageTitle = `${quiz.title} | BookQuiz`;
  const quizPageDescription = `Test your knowledge of ${/* istanbul ignore next -- book is always set alongside quiz */ book?.title || 'this book'} with this ${quiz.difficulty} quiz. ${questions.length} questions.`;

  if (!quizStarted) {
    return (
      <Layout>
        <SEOHead title={quizPageTitle} description={quizPageDescription} url={`/quiz/${quizId}`} />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="mb-6 min-h-[44px] touch-manipulation hover:bg-blue-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            <Card className="bg-white/80 backdrop-blur-sm shadow-2xl border-0">
              <CardHeader className="text-center pb-6">
                <div className="mb-4">
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-2">
                    Quiz
                  </Badge>
                </div>
                <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                  {quiz.title}
                </CardTitle>
                <p className="text-gray-600 text-lg">{quiz.description}</p>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {challenge && (
                  <div className="rounded-xl border border-purple-200 bg-purple-50 p-5 text-center">
                    <Swords className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <p className="font-semibold text-purple-900">Friend challenge</p>
                    <p className="text-purple-700">Beat {challenge.target_score}% ({challenge.correct}/{challenge.total} correct) to win.</p>
                  </div>
                )}
                {challengeUnavailable && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                    This challenge link is no longer available. You can still take the quiz.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">{quiz.time_limit} minutes</div>
                    <div className="text-sm text-gray-600">Time Limit</div>
                  </div>
                  
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <CheckCircle className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">{questions.length} questions</div>
                    <div className="text-sm text-gray-600">Total Questions</div>
                  </div>
                  
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <Badge className={`${
                      quiz.difficulty === 'Easy' ? 'bg-green-100 text-green-800 border-green-200' :
                      quiz.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                      'bg-red-100 text-red-800 border-red-200'
                    }`}>
                      {quiz.difficulty}
                    </Badge>
                    <div className="text-sm text-gray-600 mt-2">Difficulty</div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-800 mb-2">Quiz Instructions:</h3>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• You have {quiz.time_limit} minutes to complete the quiz</li>
                    <li>• You can navigate between questions using the Previous/Next buttons</li>
                    <li>• Make sure to answer all questions before submitting</li>
                    <li>• Your progress will be saved automatically</li>
                  </ul>
                </div>

                <Button 
                  onClick={startQuiz}
                  className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all hover:scale-105 shadow-lg"
                >
                  {challenge ? "Accept Challenge" : "Start Quiz"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <Layout>
      <SEOHead title={quizPageTitle} description={quizPageDescription} url={`/quiz/${quizId}`} />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-6 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Quiz Header */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{quiz.title}</h1>
                <p className="text-gray-600">{/* istanbul ignore next -- book is always set alongside quiz */ book?.title || 'Quiz'}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center text-lg font-semibold text-gray-900 mb-1">
                  <Clock className="h-5 w-5 mr-2 text-blue-600" />
                  {formatTime(timeLeft)}
                </div>
                <div className="text-sm text-gray-600">Time Remaining</div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Question {currentQuestion + 1} of {questions.length}</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </div>

          {/* Question Card */}
          {currentQ && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-6">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">
                  {currentQ.question}
                </CardTitle>
                <Badge variant="outline" className="w-fit">
                  {currentQ.type === 'multiple_choice' ? 'Multiple Choice' : 'True/False'}
                </Badge>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {currentQ.type === 'multiple_choice' ? (
                  <div className="space-y-3">
                    {currentQ.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswerSelect(index)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                          answers[currentQ.id] === index
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                            answers[currentQ.id] === index
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300'
                          }`}>
                            {answers[currentQ.id] === index && (
                              <CheckCircle className="h-4 w-4 text-white" />
                            )}
                          </div>
                          <span className="font-medium">{option}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[
                      { value: true, label: 'True' },
                      { value: false, label: 'False' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleAnswerSelect(option.value)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                          answers[currentQ.id] === option.value
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                            answers[currentQ.id] === option.value
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300'
                          }`}>
                            {answers[currentQ.id] === option.value && (
                              <CheckCircle className="h-4 w-4 text-white" />
                            )}
                          </div>
                          <span className="font-medium text-lg">{option.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
            {/* Question indicators - Hidden on mobile, shown on larger screens */}
            <div className="hidden sm:flex space-x-2 order-2 sm:order-1">
              {questions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuestion(index)}
                  className={`w-8 h-8 rounded-full text-sm font-medium transition-all hover:scale-110 ${
                    index === currentQuestion
                      ? 'bg-blue-600 text-white shadow-lg'
                      : answers[questions[index].id] !== undefined
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {/* Mobile-first navigation buttons */}
            <div className="flex justify-between w-full sm:w-auto space-x-4 order-1 sm:order-2">
              <Button
                onClick={handlePreviousQuestion}
                disabled={currentQuestion === 0}
                variant="outline"
                className="flex-1 sm:flex-none hover:scale-105 transition-all min-h-[48px] touch-manipulation"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>

              {currentQuestion === questions.length - 1 ? (
                <Button
                  onClick={handleSubmitQuiz}
                  className="flex-1 sm:flex-none bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 transition-all hover:scale-105 shadow-lg min-h-[48px] touch-manipulation"
                >
                  Submit Quiz
                </Button>
              ) : (
                <Button
                  onClick={handleNextQuestion}
                  className="flex-1 sm:flex-none hover:scale-105 transition-all min-h-[48px] touch-manipulation"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>

            {/* Mobile question indicator */}
            <div className="sm:hidden flex justify-center space-x-2 order-3 overflow-x-auto pb-2">
              {questions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuestion(index)}
                  className={`w-11 h-11 flex-shrink-0 rounded-full text-sm font-medium transition-all touch-manipulation ${
                    index === currentQuestion
                      ? 'bg-blue-600 text-white shadow-lg'
                      : answers[questions[index].id] !== undefined
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Quiz;